import { useEffect, useState } from "react"
import { Link } from "@inertiajs/react"
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageSquare,
  Play,
  Sparkles,
  Upload,
  XCircle,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { SuiteHealthSkill } from "@/types/agent_testing"
import type { TestRunDetail } from "@/types/test_lab"

type Scenario = SuiteHealthSkill["scenarios"][number]

type Props = {
  skill: SuiteHealthSkill
  scenario: Scenario
  // Caller wires this to the existing rerunScenario handler in Index.tsx.
  onRunScenario: () => void
  isRunBusy: boolean
}

// Right-pane scenario detail. Shows what we already know from
// suite_health up front, then lazy-loads run-level metadata + assertion
// verdicts via GET /skills/:skill_id/test_runs/:run_id (existing JSON
// endpoint). Falls back to "no run yet" empty state when latest_run_id
// is null.
export function ScenarioDetailPane({ skill, scenario, onRunScenario, isRunBusy }: Props) {
  const [run, setRun] = useState<TestRunDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset + (re)fetch when scenario or its latest run id changes.
  useEffect(() => {
    setRun(null)
    setError(null)
    if (!scenario.latest_run_id) return
    let cancelled = false
    setLoading(true)
    fetch(`/skills/${skill.skill_id}/test_runs/${scenario.latest_run_id}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          const data = await res.json().catch(() => ({} as { error?: string }))
          setError(data.error || `HTTP ${res.status}`)
          return
        }
        const json = (await res.json()) as TestRunDetail
        if (!cancelled) setRun(json)
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message || "Network error")
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [skill.skill_id, scenario.id, scenario.latest_run_id])

  const status = scenario.latest_status
  const isFailed = status === "failed" || status === "errored"
  const verdicts = run?.verdicts ?? []
  const failed = verdicts.filter((v) => !v.passed)
  const passing = verdicts.filter((v) => v.passed)

  // Both "Open in Test Lab" and "Promote change" land in the same Test Lab
  // page; for failed runs we add ?fix_run_id so Forge auto-seeds.
  const testLabHref = isFailed && scenario.latest_run_id
    ? `/skills/${skill.skill_id}/test?fix_run_id=${scenario.latest_run_id}`
    : `/skills/${skill.skill_id}/test`

  return (
    <section className="valet-panel overflow-hidden rounded-card">
      {/* Header */}
      <div className="border-b border-border p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <StatusBadge status={status} />
            <div className="min-w-0">
              <h2 className="text-xl font-semibold tracking-tight text-app-text sm:text-2xl">
                {scenario.name || scenario.key}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="rounded bg-white/[.06] px-2 py-1 font-mono text-[11px] text-app-muted">
                  {scenario.key}
                </code>
                <StatusChip status={status} />
              </div>
            </div>
          </div>
        </div>

        {/* Metadata grid */}
        <div className="mt-5 grid gap-3 rounded-2xl border border-border bg-white/[.025] p-4 sm:grid-cols-2 xl:grid-cols-4">
          <Meta label="Mode" value={scenario.mode} />
          <Meta
            label="Last run"
            value={scenario.latest_run_at ? fmtCompactRelative(scenario.latest_run_at) : "Never"}
          />
          <Meta
            label="Duration"
            value={run?.durationMs != null ? `${(run.durationMs / 1000).toFixed(1)}s` : "—"}
          />
          <Meta
            label="Cost"
            value={run?.costUsd != null ? `$${run.costUsd.toFixed(4)}` : "—"}
          />
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRunScenario}
            disabled={isRunBusy}
          >
            {isRunBusy
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Queueing…</>
              : <><Play size={14} className="mr-1" /> Run scenario</>}
          </Button>
          <Link
            href={testLabHref}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-control border border-border bg-white/[.04] px-3 text-sm text-app-text transition hover:bg-white/[.07]"
          >
            <ExternalLink size={14} /> Open in Test Lab
          </Link>
          {scenario.latest_run_id && (
            <Link
              href={testLabHref}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-control border border-border bg-white/[.04] px-3 text-sm text-app-text transition hover:bg-white/[.07]"
            >
              <MessageSquare size={14} /> View conversation
            </Link>
          )}
          {isFailed && (
            <Link
              href={testLabHref}
              className="inline-flex min-h-9 items-center gap-1.5 rounded-control border border-brand-redBorder bg-brand-redSoft px-3 text-sm font-semibold text-brand-red transition hover:bg-brand-redSoft/80"
            >
              <Upload size={14} /> Promote change
            </Link>
          )}
        </div>
      </div>

      {/* Body — assertion verdicts */}
      <div className="thin-scrollbar max-h-[680px] space-y-5 overflow-auto p-5">
        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} />}
        {!loading && !error && !scenario.latest_run_id && <NoRunYet onRun={onRunScenario} isBusy={isRunBusy} />}

        {!loading && !error && scenario.latest_run_id && (
          <>
            {failed.length > 0 && (
              <AssertionsBlock
                title="Failed assertions"
                count={failed.length}
                tone="error"
                verdicts={failed}
              />
            )}

            {passing.length > 0 && (
              <AssertionsBlock
                title="Passing assertions"
                count={passing.length}
                tone="success"
                verdicts={passing}
              />
            )}

            {verdicts.length === 0 && run && (
              <p className="text-sm text-app-muted">No assertion verdicts recorded for this run.</p>
            )}

            {/* Suggested-fix callout (only on failures) */}
            {isFailed && run && (
              <section className="rounded-2xl border border-vip-goldSoft bg-vip-goldSoft/40 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-vip-gold">
                      <Sparkles size={14} /> Suggested fix
                    </div>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-app-text/90">
                      {run.summary || "Open this run in the Test Lab and discuss it with Forge — we'll surface the failing assertion plus the recorded transcript so you can iterate on the workflow."}
                    </p>
                  </div>
                  <Link
                    href={testLabHref}
                    className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-control border border-vip-goldSoft bg-black/20 px-4 text-sm font-semibold text-vip-gold transition hover:bg-black/30"
                  >
                    <Sparkles size={14} /> Discuss with Forge
                  </Link>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </section>
  )
}

function StatusBadge({ status }: { status: string | null }) {
  if (status === "passed") {
    return (
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-status-success/40 bg-status-success/10 text-status-success">
        <CheckCircle2 size={22} />
      </div>
    )
  }
  if (status === "failed") {
    return (
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-status-error/40 bg-status-error/10 text-status-error">
        <XCircle size={22} />
      </div>
    )
  }
  if (status === "errored") {
    return (
      <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-status-warning/40 bg-status-warning/10 text-status-warning">
        <AlertCircle size={22} />
      </div>
    )
  }
  return (
    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-border bg-white/[.04] text-app-muted">
      <span className="text-sm">—</span>
    </div>
  )
}

function StatusChip({ status }: { status: string | null }) {
  if (status === "passed")  return <span className="rounded-full border border-status-success/40 bg-status-success/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-status-success">Passed</span>
  if (status === "failed")  return <span className="rounded-full border border-status-error/40 bg-status-error/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-status-error">Failed</span>
  if (status === "errored") return <span className="rounded-full border border-status-warning/40 bg-status-warning/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-status-warning">Errored</span>
  return <span className="rounded-full border border-border bg-white/[.04] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-app-muted">Never run</span>
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-app-muted">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-app-text">{value}</div>
    </div>
  )
}

function AssertionsBlock({
  title, count, tone, verdicts,
}: {
  title: string
  count: number
  tone: "error" | "success"
  verdicts: TestRunDetail["verdicts"]
}) {
  const toneClasses = tone === "error"
    ? { wrap: "border-status-error/40 bg-status-error/10", icon: "text-status-error", chip: "bg-status-error/15 text-status-error" }
    : { wrap: "border-status-success/40 bg-status-success/10", icon: "text-status-success", chip: "bg-status-success/15 text-status-success" }

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-base font-semibold text-app-text sm:text-lg">{title}</h3>
        <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold", toneClasses.chip)}>
          {count}
        </span>
      </div>
      <div className="space-y-3">
        {verdicts.map((v) => (
          <article
            key={v.id ?? `${v.text}-${v.kind}`}
            className={cn("rounded-2xl border p-4", toneClasses.wrap)}
          >
            <div className="flex items-start gap-3">
              <span className={cn(
                "mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border bg-black/20",
                tone === "error" ? "border-status-error/40" : "border-status-success/40",
                toneClasses.icon,
              )}>
                {tone === "error" ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-border bg-white/[.06] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-app-muted">
                    {labelForKind(v.kind)}
                  </span>
                  <h4 className="text-sm font-semibold leading-6 text-app-text sm:text-base">
                    {v.text}
                  </h4>
                </div>
                {v.reason && (
                  <p className="mt-3 text-sm italic leading-6 text-app-muted">
                    {v.reason}
                  </p>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

// Maps the assertion `kind` to the human-readable label that goes in the
// little chip on each verdict card. Backend uses "must" / "must_not" or
// the legacy "success" / "failure"; both render naturally.
function labelForKind(kind: string): string {
  switch (kind) {
    case "must":     return "Must"
    case "must_not": return "Must not"
    case "success":  return "Should"
    case "failure":  return "Should not"
    default:         return kind.replace(/_/g, " ")
  }
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-white/[.025] p-8 text-sm text-app-muted">
      <Loader2 size={16} className="animate-spin" />
      Loading run details…
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-status-error/40 bg-status-error/10 p-4 text-sm text-status-error">
      Couldn't load run details: {message}
    </div>
  )
}

function NoRunYet({ onRun, isBusy }: { onRun: () => void; isBusy: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-white/[.025] p-6 text-center text-sm text-app-muted">
      <p className="mb-3">This scenario hasn't run against the live skill version yet.</p>
      <Button variant="red" size="sm" onClick={onRun} disabled={isBusy}>
        {isBusy ? "Queueing…" : "Run now"}
      </Button>
    </div>
  )
}
